import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { authService } from "../../../utils/authService";
import MaintenanceCorrectivePDF from "./PDF/MaintenanceCorrectivePDF";
import axiosInstance from "../../../utils/axiosConfig";
import { useTranslation } from "react-i18next";
import { scheduleData } from "../utils/constants";

export const ScheduleForm = ({
  selectedSchedule,
  formData,
  setFormData,
  handleSubmit,
  users,
  clients,
  setModalIsOpen,
  currentUser,
}) => {
  const { t } = useTranslation();
  const [clientStructure, setClientStructure] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [technicianSignature, setTechnicianSignature] = useState(null);

  useEffect(() => {
    const fetchTechnicianSignature = async () => {
      const response = await axiosInstance.get(
        `/users/${selectedSchedule.user_id}`
      );
      const userData = await response.data;
      setTechnicianSignature(userData.signature_url);
    };

    if (selectedSchedule?.user_id) {
      fetchTechnicianSignature();
    }
  }, [selectedSchedule]);

  useEffect(() => {
    const fetchMaterials = async () => {
      const token = authService.getToken();
      const response = await axiosInstance.get("/materials");
      const data = response.data;
      setMaterials(data);
    };
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (selectedSchedule && selectedSchedule.schedule_materials) {
      const materialIds = selectedSchedule.schedule_materials.map(
        (sm) => sm.material_id
      );
      setSelectedMaterials(materialIds);

      const materialsObj = {};
      selectedSchedule.schedule_materials.forEach((sm) => {
        materialsObj[sm.material_id] = sm.quantity_used;
      });
      setFormData((prevData) => ({
        ...prevData,
        materials: materialsObj,
      }));
    }
  }, [selectedSchedule, setFormData]);

  const handleDelete = async () => {
    if (window.confirm(t("areYouSureDeleteSchedule"))) {
      try {
        await axiosInstance.delete(`/schedules/${selectedSchedule.id}`);
        setModalIsOpen(false);
        window.location.reload();
      } catch (error) {
        console.error("Error deleting schedule:", error);
      }
    }
  };

  const handleRemoveMaterial = async (materialId) => {
    if (selectedSchedule) {
      const token = authService.getToken();
      try {
        await axiosInstance.delete(
          `/schedules/${selectedSchedule.id}/materials/${materialId}`
        );

        const updatedMaterials = { ...formData.materials };
        delete updatedMaterials[materialId];
        setFormData({ ...formData, materials: updatedMaterials });

        setSelectedMaterials(
          selectedMaterials.filter((id) => id !== materialId)
        );

        const response = await axiosInstance.get("/materials");
        const data = response.data;
        setMaterials(data);
      } catch (error) {
        console.error("Error removing material:", error);
      }
    }
  };

  useEffect(() => {
    if (formData.client_id) {
      const fetchClientStructure = async () => {
        const { data } = await axiosInstance.get(
          `/clients/${formData.client_id}/structure`
        );
        console.log("Client structure:", data);
        setClientStructure(data);
      };
      fetchClientStructure();
    }
  }, [formData.client_id]);

  const scheduleData_ = scheduleData(selectedSchedule, technicianSignature);

  return (
    <Grid container spacing={2}>
      <form onSubmit={handleSubmit} className="form-container">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {selectedSchedule?.service_started_at && (
            <Typography variant="body1" sx={{ mt: 1 }}>
              {t("serviceStarted")}:{" "}
              {new Date(selectedSchedule.service_started_at).toLocaleString()}
            </Typography>
          )}
          {selectedSchedule?.service_completed_at && (
            <Typography variant="body1" sx={{ mt: 1 }}>
              {t("serviceCompleted")}:{" "}
              {new Date(selectedSchedule.service_completed_at).toLocaleString()}
            </Typography>
          )}
        </div>

        <Typography variant="h5" className="form-title">
          {selectedSchedule
            ? `${t("editSchedule")} ${selectedSchedule?.id}`
            : `${t("newSchedule")} ${selectedSchedule?.id}`}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <div className="form-group">
              <label>{t("user")}</label>
              <select
                value={formData.user_id || ""}
                onChange={(e) =>
                  setFormData({ ...formData, user_id: e.target.value })
                }
              >
                <option value="">{t("selectUser")}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} - {user.email}
                  </option>
                ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={12} sm={6}>
            <div className="form-group">
              <label>{t("client")}</label>
              <select
                value={formData.client_id || ""}
                onChange={(e) =>
                  setFormData({ ...formData, client_id: e.target.value })
                }
              >
                <option value="">{t("selectClient")}</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.email}
                  </option>
                ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={12} sm={6}>
            <div className="form-group">
              <label>{t("serviceType")}</label>
              <select
                value={formData.service_type}
                onChange={(e) =>
                  setFormData({ ...formData, service_type: e.target.value })
                }
                required
              >
                <option value="">{t("selectType")}</option>
                <option value="preventivo">Preventivo Ascensor</option>
                <option value="correctivo">Correctivo Ascensor</option>
                <option value="instalacion">Instalación Ascensor</option>
                <option value="electric_plant">
                  Preventivo Planta Electrica
                </option>
                <option value="electric_plant_correctivo">
                  Correctivo Planta Electrica
                </option>
                <option value="electric_plant_instalacion">
                  Instalación Planta Electrica
                </option>
                <option value="vehicular_door">
                  Preventivo Puerta Vehicular
                </option>
                <option value="vehicular_door_correctivo">
                  Correctivo Puerta Vehicular
                </option>
                <option value="vehicular_door_instalacion">
                  Instalación Puerta Vehicular
                </option>
                <option value="escalator">
                  Preventivo Escalera electrica
                </option>
                <option value="escalator_correctivo">
                  Correctivo Escalera electrica
                </option>
                <option value="escalator_instalacion">
                  Instalación Escalera electrica
                </option>
              </select>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className="form-group">
              <label>{t("location")}</label>
              <select
                value={formData.location_id || ""}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    location_id: e.target.value,
                    building_id: "",
                    elevator_id: "",
                  });
                }}
              >
                <option value="">{t("selectLocation")}</option>
                {clientStructure?.locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={12} sm={6}>
            <div className="form-group">
              <label>{t("building")}</label>
              <select
                value={formData.building_id || ""}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    building_id: e.target.value,
                    elevator_id: "",
                  });
                }}
              >
                <option value="">{t("selectBuilding")}</option>
                {clientStructure?.locations
                  .find((l) => l.id === Number(formData.location_id))
                  ?.buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={12} sm={6}>
            <div className="form-group">
              <label>{t("elevator")}</label>
              <select
                value={formData.elevator_id || ""}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    elevator_id: e.target.value,
                  });
                }}
              >
                <option value="">{t("selectElevator")}</option>
                {clientStructure?.locations
                  .find((l) => l.id === Number(formData.location_id))
                  ?.buildings.find((b) => b.id === Number(formData.building_id))
                  ?.elevators.map((elevator) => (
                    <option key={elevator.id} value={elevator.id}>
                      {elevator.identifier} - {elevator.capacity}kg
                    </option>
                  ))}
              </select>
            </div>
          </Grid>

          <Grid item xs={12} sm={12}>
            <div className="form-group">
              <label>{t("startTime")}</label>
              <input
                type="datetime-local"
                value={formData?.start_time}
                onChange={(e) =>
                  setFormData({ ...formData, start_time: e.target.value })
                }
                required
              />
            </div>
          </Grid>

          <Grid item xs={12} sm={12}>
            <div className="form-group">
              <label>{t("endTime")}</label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) =>
                  setFormData({ ...formData, end_time: e.target.value })
                }
                required
              />
            </div>
          </Grid>

          {selectedSchedule && (
            <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="materials-label">
                    {t("materialsNeeded")}
                  </InputLabel>
                  <Select
                    labelId="materials-label"
                    id="materials-select"
                    name="materials"
                    label={t("materialsNeeded")}
                    multiple
                    value={selectedMaterials}
                    onChange={(e) => setSelectedMaterials(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5,
                          width: 250,
                        },
                      },
                      style: { zIndex: 9999 },
                      container: document.body,
                    }}
                  >
                    {materials.map((material) => (
                      <MenuItem key={material.id} value={material.id}>
                        {material.name} ({t("available")}: {material.quantity})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedMaterials.map((materialId) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={materialId}
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <TextField
                    fullWidth
                    type="number"
                    label={`${t("quantityFor")} ${
                      materials.find((m) => m.id === materialId)?.name
                    }`}
                    defaultValue={
                      selectedSchedule?.schedule_materials?.find(
                        (sm) => sm.material_id === materialId
                      )?.quantity_used
                    }
                    InputProps={{
                      inputProps: {
                        min: 1,
                        max: materials.find((m) => m.id === materialId)
                          ?.quantity,
                      },
                    }}
                    onChange={(e) => {
                      const newMaterials = formData.materials || {};
                      newMaterials[materialId] = parseInt(e.target.value);
                      setFormData({ ...formData, materials: newMaterials });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveMaterial(materialId)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {t("remove")}
                  </button>
                </Grid>
              ))}
            </>
          )}

          <Grid item xs={12}>
            <div className="form-group">
              <label>{t("description")}</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t("enterScheduleDescription")}
                required
              />
            </div>
          </Grid>
        </Grid>

        <div className="modal-buttons">
          <button
            type="button"
            onClick={() => setModalIsOpen(false)}
            style={{
              padding: "8px 16px",
              marginRight: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: "#fff",
              color: "#333",
              cursor: "pointer",
            }}
          >
            {t("cancel")}
          </button>
          {selectedSchedule && currentUser === "admin" && (
            <>
              <button
                type="button"
                onClick={handleDelete}
                style={{
                  padding: "8px 16px",
                  marginRight: "10px",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                {t("deleteSchedule")}
              </button>
              <MaintenanceCorrectivePDF
                data={scheduleData_}
                service_type={selectedSchedule.service_type}
              />
            </>
          )}
          <button
            type="submit"
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              backgroundColor: "#29474d",
              color: "white",
              cursor: "pointer",
            }}
          >
            {selectedSchedule ? t("updateSchedule") : t("createSchedule")}
          </button>
        </div>
      </form>
    </Grid>
  );
};
